import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { renderWhatsappText } from './hubTemplates';
import { FlowModeControl } from './FlowModeControl';
import { StatusPill, UI, denseInputSx, primaryButtonSx, textButtonSx } from './settingsUi';
import { WebsiteCmsService } from '../../services/websiteCmsService';
import type { CmsProject } from '../../types/cms';
import {
  DEFAULT_AUTO_REPLY_SETTINGS,
  WHATSAPP_AUTO_REPLY_DOC,
  WHATSAPP_SETTINGS_COLLECTION,
  isFlowEnabled,
  getFlowMode,
  normalizeAutoReplySettings,
} from '../../utils/whatsappWorkingHours';
import type { FlowMode, WhatsappAutoReplySettings } from '../../utils/whatsappWorkingHours';
import {
  AUTO_REPLY_DEFAULTS_DOC,
  AUTO_REPLY_FLOWS,
  AUTO_REPLY_LANGS,
  AUTO_REPLY_MESSAGES_DOC,
  AUTO_REPLY_VERSIONS_SUBCOLLECTION,
  TEXT_TRIGGER_FLOWS,
  TRIGGER_PHRASE_MAX_CHARS,
  TRIGGER_PHRASES_MAX,
  OPT_IN_KEYWORDS,
  OPT_OUT_KEYWORDS,
  FOOTER_KEY,
  labelMaxChars,
  messageMaxChars,
  projectCoordinates,
  PLACEHOLDERS,
  builtInContent,
  buttonKey,
  contentEquals,
  mergeContent,
  messageButtons,
  placeholderValues,
  renderTemplate,
  textKey,
  validateContent,
} from '../../utils/whatsappAutoReplyFlows';
import type {
  AutoReplyContent,
  AutoReplyLang,
  ContentIssue,
  FlowDef,
  FlowMessageDef,
} from '../../utils/whatsappAutoReplyFlows';

const GOLD = UI.gold;
const DARK = UI.ink;
const MUTED = UI.muted;
const BORDER = UI.line;
const PAGE_BG = UI.canvas;
const MONO = UI.mono;

const TEST_PHONE_KEY = 'swarna_wa_autoreply_test_phone';
const SAMPLE_SLOTS = ['Today 4:00 PM', 'Tomorrow 11:00 AM', 'Tomorrow 4:00 PM'];

interface PublishedMeta {
  version: number;
  publishedAt: Date | null;
  publishedByEmail: string | null;
}

interface VersionRow {
  id: string;
  version: number;
  note: string;
  publishedAt: Date | null;
  publishedByEmail: string | null;
  content: AutoReplyContent;
}

const toDate = (value: any): Date | null => (value?.toDate ? value.toDate() : null);
const formatDateTime = (date: Date | null) =>
  date ? date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const readStoredPhone = () => {
  try {
    return localStorage.getItem(TEST_PHONE_KEY) || '';
  } catch {
    return '';
  }
};

const StepCard = ({
  kind,
  color,
  selected,
  onClick,
  title,
  children,
  badge,
}: {
  kind: string;
  color: string;
  selected?: boolean;
  onClick?: () => void;
  title?: string;
  children?: React.ReactNode;
  badge?: string;
}) => (
  <Box
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick ?
        (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        } :
        undefined
    }
    sx={{
      bgcolor: selected ? '#fffdf8' : UI.surface,
      border: `1px solid ${selected ? GOLD : BORDER}`,
      boxShadow: selected ? `0 0 0 3px ${GOLD}1f` : 'none',
      borderRadius: 1.5,
      px: 1.5,
      py: 1.1,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 140ms cubic-bezier(0.25, 1, 0.5, 1), border-color 140ms cubic-bezier(0.25, 1, 0.5, 1)',
      '&:hover': onClick && !selected ? { borderColor: UI.faint } : undefined,
      '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
    }}
  >
    <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: title || children ? 0.4 : 0 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 10.5, letterSpacing: 0.8, color: MUTED, fontWeight: 600 }}>{kind}</Typography>
      {badge && <Box sx={{ fontSize: 10.5, color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 1, px: 0.5, lineHeight: 1.5 }}>{badge}</Box>}
    </Stack>
    {title && <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DARK, mb: 0.25 }}>{title}</Typography>}
    {children}
  </Box>
);

/** Down arrow between step cards. (MUI reads `width: 1` as 100%, so sizes are in px.) */
const Connector = () => (
  <Box aria-hidden sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
    <svg width="12" height="26" viewBox="0 0 12 26" fill="none">
      <line x1="6" y1="0" x2="6" y2="21" stroke="#c9c3ba" strokeWidth="1.5" />
      <path d="M1.5 19.5 L6 25 L10.5 19.5" stroke="#c9c3ba" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </Box>
);

interface AutoReplyMessagesSectionProps {
  /** Selected flow when the hub controls selection. */
  flowKey?: string;
  /** Hide the internal flow list (the hub shows its own). */
  hideFlowNav?: boolean;
  /** Show the editing controls inside the customer-flow diagram without duplicating its step cards. */
  editorOnly?: boolean;
  /** Message card selected from the customer-flow diagram. */
  selectedMessageKey?: string;
}

const AutoReplyMessagesSection = ({ flowKey: controlledFlowKey, hideFlowNav = false, editorOnly = false, selectedMessageKey }: AutoReplyMessagesSectionProps) => {
  const { currentUser } = useAuth();
  const builtIn = useMemo(() => builtInContent(), []);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState<WhatsappAutoReplySettings>(DEFAULT_AUTO_REPLY_SETTINGS);
  const [projects, setProjects] = useState<CmsProject[]>([]);
  const [defaults, setDefaults] = useState<AutoReplyContent>(builtIn);
  const [draft, setDraft] = useState<AutoReplyContent>(builtIn);
  const [savedDraft, setSavedDraft] = useState<AutoReplyContent>(builtIn);
  const [published, setPublished] = useState<AutoReplyContent | null>(null);
  const [publishedMeta, setPublishedMeta] = useState<PublishedMeta | null>(null);
  const [flowKey, setFlowKey] = useState(AUTO_REPLY_FLOWS[0].key);
  const [messageKey, setMessageKey] = useState(AUTO_REPLY_FLOWS[0].messages[0].key);
  const [lang, setLang] = useState<AutoReplyLang>('en');
  const [previewProjectId, setPreviewProjectId] = useState('');
  const [previewName, setPreviewName] = useState('Amit');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'save' | 'publish' | 'default' | 'test' | null>(null);
  // Problems show after the first publish / set-default attempt and then update live while editing.
  const [showIssues, setShowIssues] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<VersionRow[] | null>(null);
  const [defaultConfirmOpen, setDefaultConfirmOpen] = useState(false);
  const [testPhone, setTestPhone] = useState(readStoredPhone);
  const [triggerInput, setTriggerInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const now = useMemo(() => new Date(), []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [hoursSnap, messagesSnap, defaultsSnap, projectList] = await Promise.all([
          getDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC)),
          getDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_MESSAGES_DOC)),
          getDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_DEFAULTS_DOC)),
          WebsiteCmsService.getAllProjects().catch(() => [] as CmsProject[]),
        ]);
        if (!active) return;
        if (hoursSnap.exists()) {
          const loadedHours = normalizeAutoReplySettings(hoursSnap.data());
          setHours(loadedHours);
          setLang(loadedHours.replyLanguage);
        }
        const defaultsContent = defaultsSnap.exists() ? mergeContent(defaultsSnap.data()?.content, builtIn) : builtIn;
        setDefaults(defaultsContent);
        const data = messagesSnap.exists() ? (messagesSnap.data() as any) : null;
        const publishedContent = data?.published ? mergeContent(data.published, defaultsContent) : null;
        const draftContent = data?.draft ? mergeContent(data.draft, defaultsContent) : publishedContent || defaultsContent;
        setPublished(publishedContent);
        setDraft(draftContent);
        setSavedDraft(draftContent);
        if (publishedContent) {
          setPublishedMeta({
            version: Number(data?.publishedVersion || 0),
            publishedAt: toDate(data?.publishedAt),
            publishedByEmail: data?.publishedByEmail || null,
          });
        }
        const visible = projectList.filter((p) => p.isPublished !== false);
        setProjects(visible);
        setPreviewProjectId(visible[0]?.id || '');
      } catch (err: any) {
        if (active) setNotice({ type: 'error', text: `Failed to load messages: ${err?.message || err}` });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [builtIn]);

  const flow: FlowDef = AUTO_REPLY_FLOWS.find((f) => f.key === flowKey) || AUTO_REPLY_FLOWS[0];
  const message: FlowMessageDef = flow.messages.find((m) => m.key === messageKey) || flow.messages[0];
  const currentTextKey = textKey(flow.key, message.key);
  const currentText = draft.texts[currentTextKey]?.[lang] || '';
  const previewProject = projects.find((p) => p.id === previewProjectId) || null;

  const values = useMemo(
    () =>
      placeholderValues({
        name: previewName,
        project: previewProject,
        exec: '',
        slot: SAMPLE_SLOTS[1],
        settings: hours,
        now,
      }),
    [previewName, previewProject, hours, now]
  );

  const issues: ContentIssue[] = useMemo(() => (showIssues ? validateContent(draft) : []), [showIssues, draft]);
  const unsavedDraft = !contentEquals(draft, savedDraft);
  const differsFromPublished = !contentEquals(draft, published);
  const messageIssues = issues.filter((i) => i.flowKey === flow.key && i.messageKey === message.key);

  useEffect(() => {
    if (!controlledFlowKey) return;
    const next = AUTO_REPLY_FLOWS.find((f) => f.key === controlledFlowKey);
    if (!next) return;
    setFlowKey(next.key);
    setMessageKey(next.messages.find((m) => m.key === selectedMessageKey)?.key || next.messages[0].key);
    setTriggerInput('');
  }, [controlledFlowKey]);

  useEffect(() => {
    if (selectedMessageKey && flow.messages.some((m) => m.key === selectedMessageKey)) setMessageKey(selectedMessageKey);
  }, [selectedMessageKey, flow]);

  // Keep master switch and flow switches live (they can change from the hub list).
  useEffect(
    () =>
      onSnapshot(
        doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC),
        (snap) => {
          if (snap.exists()) setHours(normalizeAutoReplySettings(snap.data()));
        },
        () => undefined
      ),
    []
  );

  const selectFlow = (key: string) => {
    const next = AUTO_REPLY_FLOWS.find((f) => f.key === key);
    if (!next) return;
    setFlowKey(next.key);
    setMessageKey(next.messages[0].key);
    setTriggerInput('');
  };

  const addTriggerPhrase = () => {
    const phrase = triggerInput.trim().replace(/\s+/g, ' ');
    if (!phrase) return;
    if (phrase.length > TRIGGER_PHRASE_MAX_CHARS) {
      setNotice({ type: 'error', text: `Use ${TRIGGER_PHRASE_MAX_CHARS} characters or fewer per phrase.` });
      return;
    }
    const existing = Object.entries(draft.triggerPhrases).flatMap(([key, values]) =>
      values.map((value) => ({ key, value: value.trim().replace(/\s+/g, ' ').toLocaleLowerCase() })));
    if (existing.some((item) => item.value === phrase.toLocaleLowerCase()) ||
        [...OPT_IN_KEYWORDS, ...OPT_OUT_KEYWORDS].some((value) => value.toLocaleLowerCase() === phrase.toLocaleLowerCase())) {
      setNotice({ type: 'error', text: 'This phrase is already used or reserved for opt-in or opt-out.' });
      return;
    }
    if ((draft.triggerPhrases[flow.key] || []).length >= TRIGGER_PHRASES_MAX) {
      setNotice({ type: 'error', text: `Use at most ${TRIGGER_PHRASES_MAX} phrases for this flow.` });
      return;
    }
    setDraft((prev) => ({ ...prev, triggerPhrases: {
      ...prev.triggerPhrases, [flow.key]: [...(prev.triggerPhrases[flow.key] || []), phrase],
    } }));
    setTriggerInput('');
  };

  const removeTriggerPhrase = (index: number) => {
    setDraft((prev) => ({ ...prev, triggerPhrases: {
      ...prev.triggerPhrases,
      [flow.key]: (prev.triggerPhrases[flow.key] || []).filter((_, i) => i !== index),
    } }));
  };

  const setText = (value: string) => {
    setDraft((prev) => ({
      ...prev,
      texts: { ...prev.texts, [currentTextKey]: { ...prev.texts[currentTextKey], [lang]: value } },
    }));
  };

  const setButtonLabel = (btnId: string, value: string) => {
    const key = buttonKey(flow.key, message.key, btnId);
    setDraft((prev) => ({
      ...prev,
      buttons: { ...prev.buttons, [key]: { ...prev.buttons[key], [lang]: value } },
    }));
  };

  const insertToken = (token: string) => {
    const el = textareaRef.current;
    const insert = `{${token}}`;
    if (!el) {
      setText(`${currentText}${insert}`);
      return;
    }
    const start = el.selectionStart ?? currentText.length;
    const end = el.selectionEnd ?? currentText.length;
    setText(currentText.slice(0, start) + insert + currentText.slice(end));
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + insert.length, start + insert.length);
    });
  };

  const resetMessage = (source: AutoReplyContent, label: string) => {
    setDraft((prev) => {
      const next: AutoReplyContent = { texts: { ...prev.texts }, buttons: { ...prev.buttons }, triggerPhrases: { ...prev.triggerPhrases } };
      next.texts[currentTextKey] = { ...source.texts[currentTextKey] };
      messageButtons(message).forEach((btn) => {
        const key = buttonKey(flow.key, message.key, btn.id);
        next.buttons[key] = { ...source.buttons[key] };
      });
      return next;
    });
    setNotice({ type: 'info', text: `"${message.label}" reset to ${label} in all languages. Save or publish to keep it.` });
  };

  const setFlowMode = async (key: string, title: string, next: FlowMode) => {
    const previous = getFlowMode(hours, key);
    if (next === previous) return;
    if (next === 'test' && hours.testNumbers.length === 0) {
      setNotice({ type: 'error', text: 'Add a test number in WhatsApp Settings before selecting Test.' });
      return;
    }
    setHours((prev) => ({ ...prev, flowModes: { ...prev.flowModes, [key]: next }, flowEnabled: { ...prev.flowEnabled, [key]: next === 'live' } }));
    try {
      await setDoc(
        doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC),
        // Test is false for the deployed legacy sender; the new sender reads flowModes.
        { flowModes: { [key]: next }, flowEnabled: { [key]: next === 'live' },
          ...(previous === 'off' && next !== 'off' ? { flowEnabledAt: { [key]: serverTimestamp() }, flowEnabledBy: { [key]: currentUser?.email || null } } : {}),
          updatedAt: serverTimestamp(), updatedByEmail: currentUser?.email || null },
        { merge: true }
      );
      setNotice({
        type: 'success',
        text: `${title} is ${next}.${hours.automationPaused && next !== 'off' ? ' All automation is paused in Settings.' : ''}`,
      });
    } catch (err: any) {
      setHours((prev) => ({ ...prev, flowModes: { ...prev.flowModes, [key]: previous }, flowEnabled: { ...prev.flowEnabled, [key]: previous === 'live' } }));
      setNotice({ type: 'error', text: `Could not switch ${title}: ${err?.message || err}` });
    }
  };

  const saveDraft = async () => {
    setBusy('save');
    try {
      await setDoc(
        doc(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_MESSAGES_DOC),
        { draft, draftUpdatedAt: serverTimestamp(), draftUpdatedByEmail: currentUser?.email || null },
        { merge: true }
      );
      setSavedDraft(draft);
      setNotice({ type: 'success', text: 'Draft saved. Customers still get the published version.' });
    } catch (err: any) {
      setNotice({ type: 'error', text: `Save failed: ${err?.message || err}` });
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    const found = validateContent(draft);
    setShowIssues(true);
    if (found.length) {
      const first = found[0];
      setFlowKey(first.flowKey);
      setMessageKey(first.messageKey);
      if (first.lang) setLang(first.lang);
      setNotice({ type: 'error', text: `Fix ${found.length} problem${found.length > 1 ? 's' : ''} before publishing.` });
      return;
    }
    setBusy('publish');
    try {
      const ref = doc(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_MESSAGES_DOC);
      const snap = await getDoc(ref);
      const version = Number(snap.data()?.publishedVersion || 0) + 1;
      const batch = writeBatch(db);
      const meta = {
        publishedVersion: version,
        publishedAt: serverTimestamp(),
        publishedByUid: currentUser?.uid || null,
        publishedByEmail: currentUser?.email || null,
        publishedNote: note.trim() || null,
      };
      batch.set(ref, { draft, published: draft, ...meta, draftUpdatedAt: serverTimestamp(), draftUpdatedByEmail: currentUser?.email || null }, { merge: true });
      batch.set(doc(ref, AUTO_REPLY_VERSIONS_SUBCOLLECTION, String(version)), { version, content: draft, note: note.trim() || null, ...meta });
      await batch.commit();
      setPublished(draft);
      setSavedDraft(draft);
      setPublishedMeta({ version, publishedAt: new Date(), publishedByEmail: currentUser?.email || null });
      setVersions(null);
      setNote('');
      setNotice({ type: 'success', text: `Published version ${version}.` });
    } catch (err: any) {
      setNotice({ type: 'error', text: `Publish failed: ${err?.message || err}` });
    } finally {
      setBusy(null);
    }
  };

  const saveAsDefault = async () => {
    setDefaultConfirmOpen(false);
    const found = validateContent(draft);
    setShowIssues(true);
    if (found.length) {
      setNotice({ type: 'error', text: 'Fix the problems in the draft before making it the default.' });
      return;
    }
    setBusy('default');
    try {
      await setDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_DEFAULTS_DOC), {
        content: draft,
        updatedAt: serverTimestamp(),
        updatedByEmail: currentUser?.email || null,
      });
      setDefaults(draft);
      setNotice({ type: 'success', text: '"Reset to default" now restores the current draft messages, buttons and text triggers.' });
    } catch (err: any) {
      setNotice({ type: 'error', text: `Could not save default: ${err?.message || err}` });
    } finally {
      setBusy(null);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    if (versions) return;
    try {
      const snap = await getDocs(
        query(
          collection(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_MESSAGES_DOC, AUTO_REPLY_VERSIONS_SUBCOLLECTION),
          orderBy('version', 'desc'),
          limit(30)
        )
      );
      setVersions(
        snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            version: Number(data.version || 0),
            note: String(data.note || ''),
            publishedAt: toDate(data.publishedAt),
            publishedByEmail: data.publishedByEmail || null,
            content: mergeContent(data.content, defaults),
          };
        })
      );
    } catch (err: any) {
      setVersions([]);
      setNotice({ type: 'error', text: `Could not load history: ${err?.message || err}` });
    }
  };

  const loadVersion = (row: VersionRow) => {
    setDraft(row.content);
    setHistoryOpen(false);
    setNotice({ type: 'info', text: `Version ${row.version} loaded into the draft. Publish to make it live again.` });
  };

  const sendTest = async () => {
    const phone = testPhone.replace(/[^\d]/g, '');
    if (phone.length < 10) {
      setNotice({ type: 'error', text: 'Enter your WhatsApp number with country code, e.g. 919876543210.' });
      return;
    }
    const to = phone.length === 10 ? `91${phone}` : phone;
    try {
      localStorage.setItem(TEST_PHONE_KEY, to);
    } catch {
      // Ignore
    }
    setBusy('test');
    try {
      const contact = await getDoc(doc(db, 'whatsappContactState', to.slice(-10)));
      const lastInbound = toDate(contact.data()?.lastInboundAt);
      if (!lastInbound || Date.now() - lastInbound.getTime() >= 24 * 60 * 60 * 1000) {
        setNotice({ type: 'error', text: 'This is a conversation message. Ask this number to send “Hi” to the business WhatsApp first, then retry within 24 hours. For a new lead who has not messaged, use the approved Enquiry welcome template.' });
        setBusy(null);
        return;
      }
    } catch (err: any) {
      setNotice({ type: 'error', text: `Could not check the 24-hour reply window: ${err?.message || err}` });
      setBusy(null);
      return;
    }
    // The footer has no message of its own: test it under the after-hours reply.
    const target = message.role === 'footer' ? { flowKey: 'afterHours', msg: AUTO_REPLY_FLOWS[0].messages[0] } : { flowKey: flow.key, msg: message };
    const body = renderTemplate(draft.texts[textKey(target.flowKey, target.msg.key)]?.[lang] || '', values);
    const footer = String(draft.texts[FOOTER_KEY]?.[lang] || '').trim();
    const labels = (btnId: string) => String(draft.buttons[buttonKey(target.flowKey, target.msg.key, btnId)]?.[lang] || '').trim();
    const brochureHeader = target.msg.attachment === 'brochure' && previewProject?.brochureUrl ?
      { type: 'document', document: { link: previewProject.brochureUrl, filename: `${previewProject.name} Brochure.pdf` } } :
      null;
    let payload: Record<string, unknown>;
    if (target.msg.buttons?.length) {
      payload = {
        text: body,
        rawMessagePayload: {
          type: 'interactive',
          interactive: {
            type: 'button',
            ...(brochureHeader ? { header: brochureHeader } : {}),
            body: { text: body },
            ...(footer ? { footer: { text: footer } } : {}),
            action: {
              buttons: target.msg.buttons.map((btn) => ({ type: 'reply', reply: { id: `test_${btn.id}`, title: labels(btn.id) } })),
            },
          },
        },
      };
    } else if (target.msg.listButton) {
      const fixed = (target.msg.fixedRows || []).map((row) => ({ id: `test_${row.id}`, title: labels(row.id) }));
      const rows = target.msg.listButton.rows === 'projects' ?
        projects.slice(0, 10 - fixed.length).map((p) => ({ id: `test_${p.id}`, title: p.name.slice(0, 24) })) :
        SAMPLE_SLOTS.map((slot, idx) => ({ id: `test_slot_${idx}`, title: slot }));
      payload = {
        text: body,
        rawMessagePayload: {
          type: 'interactive',
          interactive: {
            type: 'list',
            body: { text: body },
            ...(footer ? { footer: { text: footer } } : {}),
            action: { button: labels(target.msg.listButton.id), sections: [{ title: 'Options', rows: [...rows, ...fixed] }] },
          },
        },
      };
    } else {
      payload = { text: body };
    }
    const coords = target.msg.sendsLocation ? projectCoordinates(previewProject) : null;
    try {
      await httpsCallable(functions, 'whatsappSendMessage')({
        to,
        ...payload,
        metadata: { autoReplyTest: true, flow: target.flowKey, message: target.msg.key, lang },
      });
      if (coords && previewProject) {
        await httpsCallable(functions, 'whatsappSendMessage')({
          to,
          text: `${previewProject.name} location`,
          rawMessagePayload: {
            type: 'location',
            location: {
              latitude: String(coords.latitude),
              longitude: String(coords.longitude),
              name: previewProject.name,
              address: previewProject.location || '',
            },
          },
          metadata: { autoReplyTest: true, flow: target.flowKey, message: `${target.msg.key}.location`, lang },
        });
      }
      setNotice({ type: 'success', text: `WhatsApp accepted the test for +${to}${target.msg.sendsLocation && !coords ? ' (no location pin: add site latitude/longitude in Website CMS)' : ''}. Check its delivery status in Messages.` });
    } catch (err: any) {
      setNotice({
        type: 'error',
        text: `Test failed: ${err?.message || err}. WhatsApp only delivers these messages if you messaged the business number in the last 24 hours, so send "hi" to it first and try again.`,
      });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={28} sx={{ color: GOLD }} />
      </Stack>
    );
  }

  // The footer is previewed under the after-hours reply.
  const previewFlowKey = message.role === 'footer' ? 'afterHours' : flow.key;
  const previewMsg = message.role === 'footer' ? AUTO_REPLY_FLOWS[0].messages[0] : message;
  const previewLabel = (btnId: string) => draft.buttons[buttonKey(previewFlowKey, previewMsg.key, btnId)]?.[lang] || '';
  const previewText = renderTemplate(draft.texts[textKey(previewFlowKey, previewMsg.key)]?.[lang] || '', values);
  const previewButtons = (previewMsg.buttons || []).map((btn) => previewLabel(btn.id));
  const previewList = previewMsg.listButton ? previewLabel(previewMsg.listButton.id) : '';
  const previewRows = previewMsg.listButton ?
    [
      ...(previewMsg.listButton.rows === 'projects' ?
        projects.slice(0, 10 - (previewMsg.fixedRows?.length || 0)).map((p) => p.name) :
        SAMPLE_SLOTS),
      ...(previewMsg.fixedRows || []).map((row) => previewLabel(row.id)),
    ] :
    [];
  const previewFooter = previewMsg.buttons?.length || previewMsg.listButton ? String(draft.texts[FOOTER_KEY]?.[lang] || '').trim() : '';
  const previewCoords = previewMsg.sendsLocation ? projectCoordinates(previewProject) : null;
  const maxChars = messageMaxChars(message);
  const timeLabel = now.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  const smallLabelSx = { fontSize: 12, fontWeight: 600, color: DARK };

  const triggerEditor = TEXT_TRIGGER_FLOWS.some((key) => key === flow.key) ? (
            <Box sx={{ mb: editorOnly ? 2 : 0, p: 1.5, border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: UI.surface }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DARK, flex: 1 }}>Customer text triggers</Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                  {(draft.triggerPhrases[flow.key] || []).length}/{TRIGGER_PHRASES_MAX}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 1 }}>
                An exact customer message starts this flow. Button taps continue to work. Add phrases in any language.
              </Typography>
              <Stack direction="row" gap={0.75}>
                <TextField size="small" fullWidth value={triggerInput}
                  onChange={(event) => setTriggerInput(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTriggerPhrase(); } }}
                  inputProps={{ 'aria-label': `New text trigger for ${flow.title}`, maxLength: TRIGGER_PHRASE_MAX_CHARS }}
                  placeholder="e.g. Send brochure" sx={denseInputSx} />
                <Button size="small" variant="outlined" onClick={addTriggerPhrase} disabled={!triggerInput.trim()} sx={textButtonSx}>Add</Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {(draft.triggerPhrases[flow.key] || []).map((phrase, index) => (
                  <Chip key={`${phrase}-${index}`} size="small" label={phrase} onDelete={() => removeTriggerPhrase(index)}
                    sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                ))}
              </Stack>
              <Stack direction="row" gap={0.5} sx={{ mt: 0.75 }}>
                <Button size="small" onClick={() => setDraft((prev) => ({ ...prev, triggerPhrases: { ...prev.triggerPhrases, [flow.key]: [...(defaults.triggerPhrases[flow.key] || [])] } }))} sx={textButtonSx}>Reset triggers to default</Button>
              </Stack>
            </Box>
  ) : null;

  return (
    <Box sx={{ bgcolor: UI.surface, border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: 'hidden' }}>
      {/* Top bar */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: DARK }}>Auto-reply messages</Typography>
        <Tooltip title={publishedMeta?.publishedAt ? `Published ${formatDateTime(publishedMeta.publishedAt)}${publishedMeta.publishedByEmail ? ` by ${publishedMeta.publishedByEmail}` : ''}` : 'Customers get nothing until you publish.'}>
          <Box>
            <StatusPill tone={publishedMeta ? 'green' : 'amber'}>{publishedMeta ? `Live · v${publishedMeta.version}` : 'Not published'}</StatusPill>
          </Box>
        </Tooltip>
        <Tooltip title="Set each flow to Off, Test, or Live">
          <Box>
            {hours.automationPaused && <StatusPill tone="amber">All automation paused</StatusPill>}
          </Box>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {differsFromPublished && (
          <Typography sx={{ fontSize: 12, color: '#8a5a12' }}>{unsavedDraft ? 'Unsaved changes' : 'Draft not published'}</Typography>
        )}
        <Button size="small" onClick={openHistory} sx={{ ...textButtonSx, color: MUTED }}>
          History
        </Button>
        <Button size="small" disabled={!!busy} onClick={() => setDefaultConfirmOpen(true)} sx={{ ...textButtonSx, color: MUTED }}>
          Set as default
        </Button>
        <Button variant="outlined" size="small" disabled={!unsavedDraft || !!busy} onClick={saveDraft} sx={textButtonSx}>
          {busy === 'save' ? 'Saving…' : 'Save draft'}
        </Button>
        <Button variant="contained" disableElevation size="small" disabled={!differsFromPublished || !!busy} onClick={publish} sx={primaryButtonSx}>
          {busy === 'publish' ? 'Publishing…' : 'Publish'}
        </Button>
      </Stack>

      {/* Publish note + test send */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ px: 2, py: 1, bgcolor: PAGE_BG, borderBottom: `1px solid ${BORDER}` }}>
        <TextField
          size="small"
          placeholder="What changed? (saved with the next publish)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ 'aria-label': 'Publish note' }}
          sx={{ ...denseInputSx, flex: 1, minWidth: 200, maxWidth: 420, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Your number must have messaged the business number in the last 24 hours.">
          <Typography sx={{ fontSize: 12, color: MUTED }}>Test to</Typography>
        </Tooltip>
        <TextField
          size="small"
          placeholder="919876543210"
          value={testPhone}
          onChange={(e) => setTestPhone(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') sendTest();
          }}
          inputProps={{ 'aria-label': 'Your WhatsApp number', inputMode: 'tel' }}
          sx={{ ...denseInputSx, width: 140, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }}
        />
        <Button variant="outlined" size="small" disabled={!!busy || !testPhone.trim()} onClick={sendTest} sx={{ ...textButtonSx, bgcolor: UI.surface }}>
          {busy === 'test' ? 'Sending…' : 'Send test'}
        </Button>
      </Stack>

      {notice && (
        <Alert severity={notice.type} onClose={() => setNotice(null)} sx={{ borderRadius: 0, fontSize: 12.5, py: 0 }}>
          {notice.text}
        </Alert>
      )}

      {/* Body: flows | steps | editor */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: editorOnly ? 'minmax(0, 1fr)' : hideFlowNav ?
            { xs: '1fr', lg: 'minmax(0, 1fr) 380px' } :
            { xs: '1fr', md: '212px minmax(0, 1fr)', xl: '212px minmax(0, 1fr) 380px' },
        }}
      >
        {!hideFlowNav && !editorOnly && (
        <Box
          component="nav"
          aria-label="Auto-reply flows"
          sx={{ bgcolor: PAGE_BG, borderRight: { md: `1px solid ${BORDER}` }, borderBottom: { xs: `1px solid ${BORDER}`, md: 'none' }, gridRow: { md: 'span 2', xl: 'auto' }, p: 0.75 }}
        >
          <List disablePadding>
            {AUTO_REPLY_FLOWS.map((f) => {
              const selected = f.key === flow.key;
              const hasIssue = issues.some((i) => i.flowKey === f.key);
              return (
                <ListItemButton
                  key={f.key}
                  selected={selected}
                  onClick={() => selectFlow(f.key)}
                  sx={{
                    borderRadius: 1.25,
                    py: 0.25,
                    pl: 1.25,
                    pr: 0.5,
                    mb: 0.25,
                    fontSize: 13,
                    color: selected ? GOLD : DARK,
                    fontWeight: selected ? 600 : 400,
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: UI.goldTint },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, opacity: isFlowEnabled(hours, f.key) ? 1 : 0.6 }}>{f.title}</Box>
                  {hasIssue && <Box aria-label="Has problems" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#c62828', mr: 0.5 }} />}
                  <FlowModeControl compact mode={getFlowMode(hours, f.key)} label={f.title}
                    onChange={(next) => setFlowMode(f.key, f.title, next)} />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
        )}

        {!editorOnly && <Box
          sx={{
            p: 2,
            backgroundImage: 'radial-gradient(#dedad4 1px, transparent 1px)',
            backgroundSize: '16px 16px',
            borderRight: hideFlowNav ? { lg: `1px solid ${BORDER}` } : { xl: `1px solid ${BORDER}` },
          }}
        >
          {triggerEditor && <Box sx={{ maxWidth: 460, mx: 'auto', mb: 1.5 }}>{triggerEditor}</Box>}
          <Stack sx={{ maxWidth: 460, mx: 'auto' }}>
            <StepCard kind="STARTS WHEN" color={isFlowEnabled(hours, flow.key) ? '#3f9a48' : UI.faint} badge={getFlowMode(hours, flow.key)}>
              <Typography sx={{ fontSize: 12.5, color: DARK, lineHeight: 1.55 }}>{flow.startsWhen}</Typography>
              {TEXT_TRIGGER_FLOWS.some((key) => key === flow.key) && (draft.triggerPhrases[flow.key] || []).length > 0 && (
                <Typography sx={{ fontSize: 12, color: DARK, lineHeight: 1.55, mt: 0.5 }}>
                  Or customer writes: {(draft.triggerPhrases[flow.key] || []).map((phrase) => `“${phrase}”`).join(' · ')}
                </Typography>
              )}
            </StepCard>
            {flow.messages.map((msg) => {
              const text = draft.texts[textKey(flow.key, msg.key)]?.[lang] || '';
              const btns = messageButtons(msg);
              return (
                <React.Fragment key={msg.key}>
                  <Connector />
                  <StepCard
                    kind={msg.role === 'footer' ? 'FOOTER' : msg.attachment === 'brochure' ? 'BROCHURE PDF + MESSAGE' : 'MESSAGE'}
                    color={GOLD}
                    title={msg.label}
                    selected={msg.key === message.key}
                    onClick={() => setMessageKey(msg.key)}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: MUTED,
                        lineHeight: 1.55,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {text}
                    </Typography>
                    {btns.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                        {btns.map((btn) => (
                          <Box
                            key={btn.id}
                            sx={{ fontSize: 11.5, color: GOLD, border: `1px solid ${GOLD}40`, bgcolor: UI.goldTint, borderRadius: 1, px: 0.75, lineHeight: 1.8 }}
                          >
                            {msg.listButton?.id === btn.id ? '☰ ' : ''}
                            {draft.buttons[buttonKey(flow.key, msg.key, btn.id)]?.[lang] || ''}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </StepCard>
                  {msg.sendsLocation && (
                    <>
                      <Connector />
                      <StepCard kind="LOCATION PIN" badge="fixed" color="#c62828">
                        <Typography sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.55 }}>
                          Site pin from Website CMS (latitude/longitude). Opens in Google Maps.
                        </Typography>
                      </StepCard>
                    </>
                  )}
                </React.Fragment>
              );
            })}
            {flow.action && (
              <>
                <Connector />
                <StepCard kind="ACTION" badge="fixed" color="#7e57c2">
                  <Typography sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.55 }}>{flow.action}</Typography>
                </StepCard>
              </>
            )}
            {flow.end && (
              <>
                <Connector />
                <Box sx={{ mx: 'auto', border: `1px dashed #cbc6bf`, borderRadius: 999, px: 1.25, py: 0.25, bgcolor: UI.surface, fontSize: 11.5, color: MUTED }}>
                  End · {flow.end}
                </Box>
              </>
            )}
          </Stack>
        </Box>}

        <Box sx={{ p: 2, borderTop: hideFlowNav ? { xs: `1px solid ${BORDER}`, lg: 'none' } : { xs: `1px solid ${BORDER}`, xl: 'none' }, minWidth: 0 }}>
          {editorOnly && triggerEditor}
          {editorOnly && (
            <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 1.5 }}>
              {flow.messages.map((msg) => (
                <Button key={msg.key} size="small" variant={message.key === msg.key ? 'contained' : 'outlined'} onClick={() => setMessageKey(msg.key)}
                  sx={message.key === msg.key ? primaryButtonSx : textButtonSx}>{msg.label}</Button>
              ))}
            </Stack>
          )}
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DARK, flex: 1, minWidth: 0 }}>{message.label}</Typography>
            <Tooltip title="Restore this message and its buttons to your saved default, all languages">
              <Button size="small" onClick={() => resetMessage(defaults, 'default')} sx={{ textTransform: 'none', color: GOLD, minWidth: 0, fontSize: 12, px: 0.75, py: 0 }}>
                Reset to default
              </Button>
            </Tooltip>
            <Tooltip title="Restore the text that shipped with the CRM, all languages">
              <Button size="small" onClick={() => resetMessage(builtIn, 'original')} sx={{ textTransform: 'none', color: MUTED, minWidth: 0, fontSize: 12, px: 0.75, py: 0 }}>
                Original
              </Button>
            </Tooltip>
          </Stack>

          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={lang}
            onChange={(_, value) => value && setLang(value)}
            aria-label="Message language"
            sx={{
              mb: 1.5,
              p: 0.25,
              bgcolor: PAGE_BG,
              borderRadius: 1.5,
              '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12.5, border: 'none', borderRadius: '6px !important', py: 0.4, color: MUTED },
              '& .Mui-selected': { bgcolor: `${UI.surface} !important`, boxShadow: `0 1px 2px ${BORDER}`, fontWeight: 600, color: `${DARK} !important` },
            }}
          >
            {AUTO_REPLY_LANGS.map((l) => (
              <ToggleButton key={l.key} value={l.key}>
                {l.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
            <Typography sx={smallLabelSx}>Message</Typography>
            <Typography sx={{ fontSize: 11.5, fontFamily: MONO, color: currentText.length > maxChars ? '#c62828' : UI.faint }}>
              {currentText.length}/{maxChars}
            </Typography>
          </Stack>
          <TextField
            multiline
            fullWidth
            minRows={message.role === 'footer' ? 2 : 6}
            maxRows={14}
            value={currentText}
            onChange={(e) => setText(e.target.value)}
            inputRef={textareaRef}
            inputProps={{ 'aria-label': `${message.label} text` }}
            sx={{ '& .MuiInputBase-root': { p: 1.25, bgcolor: UI.surface }, '& .MuiInputBase-input': { fontSize: 13, lineHeight: 1.55 } }}
          />
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
            {message.placeholders.map((token) => {
              const def = PLACEHOLDERS.find((p) => p.key === token);
              const required = message.required.includes(token);
              return (
                <Tooltip key={token} title={`${def?.description || ''}${required ? ' (required)' : ''}. Click to insert.`}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => insertToken(token)}
                    sx={{
                      fontFamily: MONO,
                      fontSize: 11.5,
                      color: GOLD,
                      bgcolor: required ? UI.goldTint : 'transparent',
                      border: `1px ${required ? 'solid' : 'dashed'} ${GOLD}66`,
                      borderRadius: 1,
                      px: 0.6,
                      py: 0.1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: UI.goldTint },
                      '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 1 },
                    }}
                  >
                    {`{${token}}`}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.75, lineHeight: 1.5 }}>
            {message.role === 'footer' ?
              'Max 60 characters, no placeholders. Leave empty for no footer. Keeping "Reply STOP" tells customers how to opt out.' :
              <>
                {message.required.length ? `Must keep ${message.required.map((t) => `{${t}}`).join(', ')}. ` : ''}
                *bold* _italic_ ~strike~. Lines with only empty placeholders are skipped.
              </>}
          </Typography>

          {messageButtons(message).length > 0 && (
            <Box sx={{ mt: 1.75 }}>
              <Typography sx={{ ...smallLabelSx, mb: 0.5 }}>{message.listButton ? 'List button and fixed rows' : 'Buttons'}</Typography>
              <Stack gap={0.75}>
                {messageButtons(message).map((btn) => {
                  const value = draft.buttons[buttonKey(flow.key, message.key, btn.id)]?.[lang] || '';
                  const max = labelMaxChars(message, btn.id);
                  const tooLong = value.length > max;
                  return (
                    <TextField
                      key={btn.id}
                      size="small"
                      value={value}
                      onChange={(e) => setButtonLabel(btn.id, e.target.value)}
                      error={tooLong || !value.trim()}
                      inputProps={{ 'aria-label': `Button ${btn.defaults.en}` }}
                      sx={denseInputSx}
                      InputProps={{
                        endAdornment: (
                          <Typography sx={{ fontSize: 11, fontFamily: MONO, color: tooLong ? '#c62828' : UI.faint }}>
                            {value.length}/{max}
                          </Typography>
                        ),
                      }}
                    />
                  );
                })}
              </Stack>
              {message.listButton && (
                <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.5 }}>
                  The other rows ({message.listButton.rows === 'projects' ? 'published projects' : 'visit slots'}) are filled by the CRM.
                  {message.fixedRows?.length ? ' Fixed rows always come last.' : ''}
                </Typography>
              )}
            </Box>
          )}

          {messageIssues.length > 0 && (
            <Alert severity="error" sx={{ mt: 1.5, fontSize: 12, py: 0 }}>
              {messageIssues.map((i) => (
                <div key={i.text}>{i.text}</div>
              ))}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 2.25, mb: 0.75 }}>
            <Typography sx={{ ...smallLabelSx, flex: 1 }}>Preview</Typography>
            <TextField
              select
              size="small"
              value={previewProjectId}
              onChange={(e) => setPreviewProjectId(e.target.value)}
              inputProps={{ 'aria-label': 'Preview project' }}
              sx={{ ...denseInputSx, width: 150 }}
            >
              {projects.length === 0 && <MenuItem value="">No projects</MenuItem>}
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              value={previewName}
              onChange={(e) => setPreviewName(e.target.value)}
              inputProps={{ 'aria-label': 'Preview customer name' }}
              sx={{ ...denseInputSx, width: 92 }}
            />
          </Stack>
          <Box sx={{ bgcolor: '#efe7dd', borderRadius: 2, p: 1.5 }}>
            <Box sx={{ maxWidth: 300, ml: 'auto' }}>
              <Box sx={{ bgcolor: '#d9fdd3', borderRadius: 1.75, px: 1.25, pt: 1, pb: 0.5, boxShadow: '0 1px 1px rgba(40,30,20,0.08)' }}>
                {previewMsg.attachment === 'brochure' && (
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ bgcolor: '#c8efc1', borderRadius: 1.25, p: 0.75, mb: 0.75 }}>
                    <DescriptionOutlinedIcon sx={{ color: '#c62828', fontSize: 20 }} />
                    <Typography sx={{ fontSize: 12, color: DARK }}>
                      {previewProject?.brochureUrl ? `${previewProject.name} Brochure.pdf` : 'No brochure on this project, text only'}
                    </Typography>
                  </Stack>
                )}
                <Typography sx={{ fontSize: 13, color: '#111b21', whiteSpace: 'pre-wrap', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {renderWhatsappText(previewText)}
                </Typography>
                {previewFooter && <Typography sx={{ fontSize: 11.5, color: '#667781', mt: 0.5 }}>{previewFooter}</Typography>}
                <Typography sx={{ fontSize: 10.5, color: '#667781', textAlign: 'right', mt: 0.25 }}>{timeLabel} ✓✓</Typography>
              </Box>
              {previewButtons.map((label, idx) => (
                <Box key={idx} sx={{ bgcolor: '#fdfdfc', borderRadius: 1.75, mt: 0.4, py: 0.7, textAlign: 'center', fontSize: 13, color: '#027eb5', boxShadow: '0 1px 1px rgba(40,30,20,0.08)' }}>
                  {label}
                </Box>
              ))}
              {previewMsg.listButton && (
                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} sx={{ bgcolor: '#fdfdfc', borderRadius: 1.75, mt: 0.4, py: 0.7, fontSize: 13, color: '#027eb5', boxShadow: '0 1px 1px rgba(40,30,20,0.08)' }}>
                  <FormatListBulletedIcon sx={{ fontSize: 16 }} />
                  {previewList}
                </Stack>
              )}
              {previewRows.length > 0 && (
                <Typography sx={{ fontSize: 11, color: '#667781', mt: 0.5, textAlign: 'center' }}>
                  Opens: {previewRows.join(' · ')}
                </Typography>
              )}
              {previewMsg.sendsLocation && (
                <Stack direction="row" alignItems="center" gap={0.75} sx={{ bgcolor: '#d9fdd3', borderRadius: 1.75, mt: 0.75, p: 1, boxShadow: '0 1px 1px rgba(40,30,20,0.08)' }}>
                  <PlaceOutlinedIcon sx={{ color: '#c62828', fontSize: 22 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#111b21' }}>{previewProject?.name || 'Project site'}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#667781' }}>
                      {previewCoords ? `${previewProject?.location || ''} · opens in Maps` : 'No pin: add site latitude/longitude in Website CMS'}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: UI.faint, mt: 0.75 }}>
            {'{next}'} = {values.next}
            {' · '}{'{exec}'} = {values.exec}
          </Typography>
        </Box>
      </Box>

      {/* Version history */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, py: 1.5 }}>Version history</DialogTitle>
        <DialogContent dividers sx={{ py: 0.5 }}>
          {!versions ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={20} sx={{ color: GOLD }} />
            </Stack>
          ) : versions.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>Nothing published yet.</Typography>
          ) : (
            <Stack>
              {versions.map((row, idx) => (
                <Stack key={row.id} direction="row" alignItems="center" gap={1.5} sx={{ py: 1, borderTop: idx ? `1px solid ${BORDER}` : 'none' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: DARK }}>v{row.version}</Typography>
                      {publishedMeta?.version === row.version && <StatusPill tone="green">Live</StatusPill>}
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {formatDateTime(row.publishedAt)} · {row.publishedByEmail || 'unknown'}
                      </Typography>
                    </Stack>
                    {row.note && <Typography sx={{ fontSize: 12.5, color: DARK, mt: 0.25 }}>{row.note}</Typography>}
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => loadVersion(row)} sx={textButtonSx}>
                    Load into draft
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setHistoryOpen(false)} sx={textButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set as default */}
      <Dialog open={defaultConfirmOpen} onClose={() => setDefaultConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, py: 1.5 }}>Make the draft the default?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: DARK, lineHeight: 1.55 }}>
            Every message, button and text trigger in the draft becomes what "Reset to default" restores. "Original" still
            restores the shipped texts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setDefaultConfirmOpen(false)} sx={textButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={saveAsDefault} sx={primaryButtonSx}>
            Set as default
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoReplyMessagesSection;
